import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Store, ShoppingBag, LogIn, LogOut, Plus, Minus, Trash2, CalendarClock, Loader2, MapPin, Search, LayoutDashboard, UserRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, peso, fmtDateTime, mediaUrl } from "../lib/api";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { useAuth } from "../context/AuthContext";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const CLIENT_KEY = "mmx_client";
const getClient = () => JSON.parse(localStorage.getItem(CLIENT_KEY) || "null");
const setClientStore = (c) => c ? localStorage.setItem(CLIENT_KEY, JSON.stringify(c)) : localStorage.removeItem(CLIENT_KEY);
const authHeaders = () => {
  const c = getClient();
  return c?.token ? { Authorization: `Bearer ${c.token}` } : {};
};

const ORDER_STATUS = {
  pending: "bg-blue-100 text-blue-700", confirmed: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-200 text-slate-600",
};

export default function PortalPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: staffUser, hydrate } = useAuth();
  const [client, setClient] = useState(getClient());
  const [stores, setStores] = useState([]);
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("customer"); // customer | staff
  const [authMode, setAuthMode] = useState("login"); // login | register | forgot | forgotDone
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [staffForm, setStaffForm] = useState({ identifier: "", password: "" });
  const [showStaffPw, setShowStaffPw] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [fulfillment, setFulfillment] = useState("pickup");
  const [apptAt, setApptAt] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [payRef, setPayRef] = useState("");
  const [myOrders, setMyOrders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [homeFeatures, setHomeFeatures] = useState([]);
  const [storeGallery, setStoreGallery] = useState({ features: [], banners: [] });

  const storeMatch = location.pathname.match(/^\/portal\/store\/([0-9a-f-]+)/);
  const storeId = storeMatch?.[1];
  const view = location.pathname === "/portal/orders" ? "orders" : storeId ? "store" : "home";

  const loadStores = useCallback((c) => {
    api.get("/portal/stores", { params: c || {} }).then(({ data }) => setStores(data)).catch(() => {});
  }, []);
  useEffect(() => { loadStores(null); }, [loadStores]);

  useEffect(() => {
    api.get("/portal/gallery").then(({ data }) => setHomeFeatures(data.features || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!storeId) return;
    setStoreGallery({ features: [], banners: [] });
    api.get(`/portal/gallery?tenant_id=${storeId}`).then(({ data }) => setStoreGallery(data)).catch(() => {});
    api.get(`/portal/stores/${storeId}/products`)
      .then(({ data }) => { setStore(data.store); setProducts(data.products); })
      .catch((e) => toast.error(fmtErr(e)));
  }, [storeId]);

  const loadMyOrders = useCallback(() => {
    if (!getClient()) return;
    api.get("/portal/my-orders", { headers: authHeaders() }).then(({ data }) => setMyOrders(data)).catch(() => {});
  }, []);
  useEffect(() => { if (view === "orders") loadMyOrders(); }, [view, loadMyOrders]);

  const openAuth = (tab, mode) => {
    setAuthTab(tab);
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/${authMode}`, {
        name: authForm.name, email: authForm.email, password: authForm.password, phone: authForm.phone || null,
      });
      const c = { token: data.token, name: data.client.name, email: data.client.email };
      setClientStore(c);
      setClient(c);
      setAuthOpen(false);
      toast.success(`Welcome, ${data.client.name}!`);
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/login", { identifier: staffForm.identifier, password: staffForm.password });
      const { data } = await api.get("/auth/me");
      hydrate(data);
      setAuthOpen(false);
      navigate("/home");
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: authForm.email });
    } catch { /* response is intentionally generic */ }
    setAuthMode("forgotDone");
    setBusy(false);
  };

  const locateMe = () => {
    if (!navigator.geolocation) { toast.error("Geolocation is not supported by this browser"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCoords(c);
        loadStores(c);
        setLocating(false);
        toast.success("Showing stores nearest to you");
      },
      () => { setLocating(false); toast.error("Location permission denied — enable it in your browser to find stores near you"); },
      { timeout: 10000 }
    );
  };

  const visibleStores = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) =>
      s.store_name.toLowerCase().includes(q) ||
      (s.address || "").toLowerCase().includes(q) ||
      (s.business_type || "").replace(/_/g, " ").includes(q));
  }, [stores, search]);

  const addToCart = (p) => {
    setCart((prev) => {
      const ex = prev.find((c) => c.product_id === p.product_id);
      const qty = (ex?.quantity || 0) + 1;
      if (qty > p.stock_quantity) { toast.error(`Only ${p.stock_quantity} available`); return prev; }
      if (ex) return prev.map((c) => c.product_id === p.product_id ? { ...c, quantity: qty } : c);
      return [...prev, { product_id: p.product_id, name: p.name, unit_price: p.retail_price, quantity: 1 }];
    });
  };
  const setQty = (id, q) => setCart((prev) => q <= 0 ? prev.filter((c) => c.product_id !== id) : prev.map((c) => c.product_id === id ? { ...c, quantity: q } : c));
  const total = useMemo(() => cart.reduce((s, c) => s + c.unit_price * c.quantity, 0), [cart]);

  const placeOrder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/orders", {
        tenant_id: storeId,
        items: cart.map((c) => ({ product_id: c.product_id, quantity: c.quantity })),
        fulfillment, appointment_at: fulfillment === "appointment" ? apptAt : null,
        delivery_address: fulfillment === "delivery" ? address : null,
        note: note || null, payment_ref: payRef || null,
      }, { headers: authHeaders() });
      toast.success(`Order ${data.order_number} placed! The store will confirm shortly.`);
      setCart([]); setCheckoutOpen(false); setNote(""); setPayRef(""); setAddress("");
      navigate("/portal/orders");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  const doLogout = () => { setClientStore(null); setClient(null); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center gap-2 sm:gap-3 px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo />
            <span className="font-heading font-bold text-lg tracking-tight text-slate-900">MatteusMx</span>
          </Link>
          <div className="flex-1" />
          {staffUser && (
            <Button size="sm" variant="outline" onClick={() => navigate("/home")} data-testid="portal-dashboard-btn">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Open</span> Dashboard
            </Button>
          )}
          {client ? (
            <>
              <Link to="/portal/orders"><Button variant="ghost" size="sm" data-testid="portal-my-orders-link"><ShoppingBag className="h-4 w-4 mr-1.5" /> My Orders</Button></Link>
              <span className="text-sm text-slate-600 hidden sm:inline">Hi, {client.name}</span>
              <Button variant="outline" size="sm" onClick={doLogout} data-testid="portal-logout-btn"><LogOut className="h-4 w-4 mr-1" /> Sign out</Button>
            </>
          ) : !staffUser && (
            <>
              <Button variant="ghost" size="sm" onClick={() => openAuth("customer", "login")} data-testid="portal-login-btn"><LogIn className="h-4 w-4 mr-1.5" /> Log in</Button>
              <Button size="sm" className="bg-slate-900" onClick={() => openAuth("customer", "register")} data-testid="portal-signup-btn"><UserRound className="h-4 w-4 mr-1.5" /> Sign up</Button>
            </>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        {view === "home" && (
          <div className="space-y-8" data-testid="portal-home">
            <div className="space-y-2">
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Order from stores near you</h1>
              <p className="text-sm text-slate-500">Pre-order for pick-up or delivery, or book an appointment — pay via QRPh and send your proof of payment.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input className="pl-9 h-11 bg-white" placeholder="Search stores by name, type, or address…"
                  value={search} onChange={(e) => setSearch(e.target.value)} data-testid="portal-search-input" />
              </div>
              <Button variant={coords ? "default" : "outline"} className={`h-11 ${coords ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                onClick={locateMe} disabled={locating} data-testid="portal-near-me-btn">
                {locating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <MapPin className="h-4 w-4 mr-1.5" />}
                {coords ? "Sorted by distance" : "Near me"}
              </Button>
            </div>
            {homeFeatures.length > 0 && (
              <Carousel className="w-full" opts={{ loop: true }} data-testid="home-feature-carousel">
                <CarouselContent>
                  {homeFeatures.map((img) => (
                    <CarouselItem key={img.image_id}>
                      <div className="relative overflow-hidden rounded-xl border border-slate-200">
                        <img src={mediaUrl(img.image_id)} alt={img.caption || "Featured"} className="w-full h-52 sm:h-72 object-cover" data-testid={`home-feature-${img.image_id}`} />
                        {img.caption && (
                          <p className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-900/70 to-transparent text-white text-sm font-medium px-4 py-3">{img.caption}</p>
                        )}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {homeFeatures.length > 1 && (<><CarouselPrevious className="left-3" /><CarouselNext className="right-3" /></>)}
              </Carousel>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleStores.map((s) => (
                <Link key={s.tenant_id} to={`/portal/store/${s.tenant_id}`} data-testid={`portal-store-${s.tenant_id}`}>
                  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2 hover:border-slate-400 hover:shadow-sm transition-all h-full">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center"><Store className="h-4 w-4 text-slate-600" /></div>
                      <p className="font-semibold text-slate-900 flex-1 truncate">{s.store_name}</p>
                      {s.distance_km != null && (
                        <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-[10px] font-bold font-mono" data-testid={`portal-store-distance-${s.tenant_id}`}>
                          {s.distance_km} km
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 capitalize">{s.business_type?.replace(/_/g, " ")}{s.address ? ` · ${s.address}` : ""}</p>
                  </div>
                </Link>
              ))}
              {visibleStores.length === 0 && <p className="text-sm text-slate-400 col-span-full py-12 text-center">{search ? `No stores match "${search}".` : "No stores available right now."}</p>}
            </div>
          </div>
        )}

        {view === "store" && store && (
          <div className="grid lg:grid-cols-3 gap-6" data-testid="portal-store-page">
            <div className="lg:col-span-2 space-y-4">
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{store.store_name}</h1>
                <p className="text-sm text-slate-500 capitalize">{store.business_type?.replace(/_/g, " ")}</p>
              </div>
              {storeGallery.banners.length > 0 && (
                <div className="space-y-2" data-testid="store-banners">
                  {storeGallery.banners.map((b) => (
                    <div key={b.image_id} className="relative">
                      <img src={mediaUrl(b.image_id)} alt={b.caption || "Promotion"} className="w-full h-20 sm:h-24 object-cover rounded-lg border border-slate-200" data-testid={`store-banner-${b.image_id}`} />
                      {b.caption && <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-semibold drop-shadow-md bg-slate-900/30 rounded-lg px-6 text-center">{b.caption}</span>}
                    </div>
                  ))}
                </div>
              )}
              {storeGallery.features.length > 0 && (
                <div className="grid grid-cols-2 gap-2" data-testid="store-features">
                  {storeGallery.features.map((f) => (
                    <img key={f.image_id} src={mediaUrl(f.image_id)} alt={f.caption || "Store photo"} className="w-full h-36 object-cover rounded-lg border border-slate-200" data-testid={`store-feature-${f.image_id}`} />
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {products.map((p) => (
                  <button key={p.product_id} onClick={() => addToCart(p)} data-testid={`portal-product-${p.product_id}`}
                    className="bg-white border border-slate-200 rounded-xl p-3 text-left hover:border-slate-400 hover:shadow-sm transition-all min-h-28 flex flex-col justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2">{p.name}</p>
                      {p.category && <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{p.category}</p>}
                    </div>
                    <div className="flex items-end justify-between">
                      <span className="font-mono font-bold text-slate-900">{peso(p.retail_price)}</span>
                      <span className="text-[11px] text-slate-400">{p.stock_quantity} left</span>
                    </div>
                  </button>
                ))}
                {products.length === 0 && <p className="col-span-full py-12 text-center text-sm text-slate-400">No items available.</p>}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 h-fit lg:sticky lg:top-24">
              <h3 className="font-heading font-semibold text-slate-900">Your Order</h3>
              {cart.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Tap items to add them.</p>}
              {cart.map((c) => (
                <div key={c.product_id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate text-slate-800">{c.name}</span>
                  <button className="w-7 h-7 rounded border border-slate-200 flex items-center justify-center" onClick={() => setQty(c.product_id, c.quantity - 1)}><Minus className="h-3 w-3" /></button>
                  <span className="w-6 text-center font-mono">{c.quantity}</span>
                  <button className="w-7 h-7 rounded border border-slate-200 flex items-center justify-center" onClick={() => setQty(c.product_id, c.quantity + 1)}><Plus className="h-3 w-3" /></button>
                  <span className="w-16 text-right font-mono font-semibold">{peso(c.unit_price * c.quantity)}</span>
                  <button className="text-slate-300 hover:text-red-600" onClick={() => setQty(c.product_id, 0)}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              <div className="border-t border-slate-100 pt-3 flex justify-between items-baseline">
                <span className="text-sm font-semibold">Total</span>
                <span className="font-mono font-bold text-xl">{peso(total)}</span>
              </div>
              <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" disabled={cart.length === 0}
                data-testid="portal-checkout-btn"
                onClick={() => client ? setCheckoutOpen(true) : openAuth("customer", "login")}>
                {client ? "Place order" : "Sign in to order"}
              </Button>
            </div>
          </div>
        )}

        {view === "orders" && (
          <div className="space-y-6" data-testid="portal-orders-page">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">My Orders</h1>
            {!client && <p className="text-sm text-slate-500">Please sign in to view your orders.</p>}
            <div className="space-y-3">
              {myOrders.map((o) => (
                <div key={o.order_id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`portal-order-${o.order_id}`}>
                  <div className="flex-1 min-w-48">
                    <p className="font-mono text-xs font-semibold text-slate-900">{o.order_number} <span className="font-sans font-normal text-slate-400">· {o.store_name}</span></p>
                    <p className="text-xs text-slate-500 mt-0.5">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{o.fulfillment}{o.appointment_at ? ` · ${fmtDateTime(o.appointment_at)}` : ""} · {fmtDateTime(o.created_at)}</p>
                  </div>
                  <span className="font-mono font-semibold text-sm">{peso(o.total_amount)}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${ORDER_STATUS[o.status]}`} data-testid={`portal-order-status-${o.order_id}`}>{o.status}</span>
                </div>
              ))}
              {client && myOrders.length === 0 && <p className="text-sm text-slate-400 py-12 text-center">No orders yet.</p>}
            </div>
          </div>
        )}
      </main>

      <Dialog open={authOpen} onOpenChange={setAuthOpen}>
        <DialogContent className="max-w-sm" data-testid="portal-auth-modal">
          <DialogHeader><DialogTitle className="font-heading">Welcome to MatteusMx</DialogTitle></DialogHeader>

          {(authMode === "forgot" || authMode === "forgotDone") ? (
            authMode === "forgotDone" ? (
              <div className="space-y-4" data-testid="portal-forgot-confirmation">
                <p className="text-sm text-slate-600 leading-relaxed">
                  If <span className="font-medium text-slate-900">{authForm.email}</span> is registered — as a customer or store staff account — a password reset link has been sent. It expires in 1 hour and can be used once.
                </p>
                <Button variant="outline" className="w-full" onClick={() => setAuthMode("login")} data-testid="portal-forgot-back-btn">Back to sign in</Button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-3" data-testid="portal-forgot-form">
                <p className="text-sm text-slate-500">Enter the email on your account. We will email you a secure link to reset your password.</p>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required data-testid="portal-forgot-email-input" />
                </div>
                <Button type="submit" className="w-full bg-slate-900" disabled={busy} data-testid="portal-forgot-submit">
                  {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Send reset link
                </Button>
                <button type="button" className="w-full text-center text-xs text-slate-500 hover:text-slate-900" onClick={() => setAuthMode("login")} data-testid="portal-forgot-cancel">Back to sign in</button>
              </form>
            )
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1" data-testid="portal-auth-tabs">
                {[["customer", "Customer"], ["staff", "Store Staff"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => { setAuthTab(v); setAuthMode("login"); }} data-testid={`portal-tab-${v}`}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${authTab === v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                    {l}
                  </button>
                ))}
              </div>

              {authTab === "customer" ? (
                <form onSubmit={submitAuth} className="space-y-3">
                  {authMode === "register" && (
                    <>
                      <div className="space-y-1.5"><Label>Full name</Label><Input value={authForm.name} onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} required data-testid="portal-reg-name" /></div>
                      <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} data-testid="portal-reg-phone" /></div>
                    </>
                  )}
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} required data-testid="portal-auth-email" /></div>
                  <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} required data-testid="portal-auth-password" /></div>
                  {authMode === "login" && (
                    <div className="text-right">
                      <button type="button" className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline" onClick={() => setAuthMode("forgot")} data-testid="portal-forgot-link">Forgot password?</button>
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-slate-900" disabled={busy} data-testid="portal-auth-submit">
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{authMode === "login" ? "Sign in" : "Create account"}
                  </Button>
                  <button type="button" className="w-full text-center text-xs text-slate-500 hover:text-slate-900"
                    onClick={() => setAuthMode(authMode === "login" ? "register" : "login")} data-testid="portal-auth-toggle">
                    {authMode === "login" ? "No account? Register" : "Have an account? Sign in"}
                  </button>
                </form>
              ) : (
                <form onSubmit={submitStaff} className="space-y-3" data-testid="portal-staff-form">
                  <p className="text-xs text-slate-500">For store owners, managers and cashiers.</p>
                  <div className="space-y-1.5"><Label>Username or Email</Label><Input value={staffForm.identifier} onChange={(e) => setStaffForm({ ...staffForm, identifier: e.target.value })} required data-testid="portal-staff-identifier-input" autoComplete="username" /></div>
                  <div className="space-y-1.5">
                    <Label>Password</Label>
                    <div className="relative">
                      <Input type={showStaffPw ? "text" : "password"} value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} required className="pr-10" data-testid="portal-staff-password-input" autoComplete="current-password" />
                      <button type="button" onClick={() => setShowStaffPw(!showStaffPw)} data-testid="portal-staff-toggle-password"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
                        {showStaffPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <button type="button" className="text-xs font-medium text-slate-500 hover:text-slate-900 hover:underline" onClick={() => setAuthMode("forgot")} data-testid="portal-staff-forgot-link">Forgot password?</button>
                  </div>
                  <Button type="submit" className="w-full bg-slate-900" disabled={busy} data-testid="portal-staff-login-btn">
                    {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Sign in to workspace
                  </Button>
                  <p className="text-center text-xs text-slate-500">
                    New business? <Link to="/register" className="font-semibold text-slate-900 hover:underline" data-testid="portal-apply-link" onClick={() => setAuthOpen(false)}>Apply for a store account</Link>
                  </p>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md" data-testid="portal-checkout-modal">
          <DialogHeader><DialogTitle className="font-heading">Checkout — {peso(total)}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Fulfillment</Label>
              <RadioGroup value={fulfillment} onValueChange={setFulfillment} className="flex gap-2 flex-wrap">
                {[["pickup", "Pick-up"], ["delivery", "Delivery"], ["appointment", "Appointment"]].map(([v, l]) => (
                  <label key={v} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${fulfillment === v ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200"}`} data-testid={`portal-fulfillment-${v}`}>
                    <RadioGroupItem value={v} className="sr-only" />{l}
                  </label>
                ))}
              </RadioGroup>
            </div>
            {fulfillment === "appointment" && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Appointment date &amp; time</Label>
                <Input type="datetime-local" value={apptAt} onChange={(e) => setApptAt(e.target.value)} data-testid="portal-appointment-input" />
              </div>
            )}
            {fulfillment === "delivery" && (
              <div className="space-y-1.5"><Label>Delivery address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="portal-address-input" /></div>
            )}
            <div className="space-y-1.5"><Label>Note for the store (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} /></div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center space-y-2">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(`QRPH|MATTEUSMX|${total.toFixed(2)}|PHP`)}`} alt="QRPh" width={120} height={120} className="mx-auto rounded border bg-white p-1.5" data-testid="portal-qrph-image" />
              <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed text-left">
                Pay via QRPh / GCash, then send your proof of payment to the store. Orders are manually confirmed by the store after payment.
              </p>
              <Input className="h-9 font-mono text-xs bg-white" placeholder="Payment reference no. (optional now)" value={payRef} onChange={(e) => setPayRef(e.target.value)} data-testid="portal-payment-ref-input" />
            </div>
            <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700" onClick={placeOrder} disabled={busy || (fulfillment === "appointment" && !apptAt) || (fulfillment === "delivery" && !address.trim())} data-testid="portal-place-order-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Place order — {peso(total)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
